import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './webdesign.css';
import Header from '../components/Navbar/Navbar.js';
import Footer from '../components/Footer/Footer.js';
import { images } from '../config/imagePaths';

const features = [
  {
    title: 'Built by professional engineers',
    text: 'Your website is designed and built by qualified software engineers with experience across leading companies and international competitions. That expertise means cleaner code, stronger performance, and a finished product you can rely on.',
    image: images.services.webDesignSpeaking,
    alt: 'An AOK engineer building a website',
  },
  {
    title: 'State-of-the-art by default',
    text: 'We build with modern frameworks and current best practices, so every site feels fast, polished, and genuinely up to date. Fully responsive across mobile, tablet, and desktop comes as standard, never as an add-on.',
    image: images.services.webDesignShowcase,
    alt: 'A modern, responsive website shown across devices',
  },
  {
    title: 'Delivered faster than the competition',
    text: 'A streamlined process backed by real engineering skill lets us turn projects around quicker than most agencies. You get online sooner, without ever trading away quality to do it.',
    image: images.services.webDesignGraphic,
    alt: 'A laptop displaying a finished website',
  },
];

const projects = [
  {
    name: 'AOK Communications',
    url: 'https://www.aokcommunications.ie/',
    domain: 'aokcommunications.ie',
    category: 'Brand & Services',
    number: '01',
    accent: '#d9efe7',
    deep: '#024437',
  },
  {
    name: 'SJF Fitness Portlaoise',
    url: 'https://www.sjffitnessportlaoise.com/',
    domain: 'sjffitnessportlaoise.com',
    category: 'Fitness & Coaching',
    number: '02',
    livePreview: true,
    accent: '#eff0e7',
    deep: '#373d2a',
  },
  {
    name: 'Ignite Event Management',
    url: 'https://www.igniteeventmanagement.com/',
    domain: 'igniteeventmanagement.com',
    category: 'Events & Experiences',
    number: '03',
    heroImage: '/images/portfolio/ignite-hero.webp',
    previewImage: '/images/portfolio/ignite-overlay.jpg',
    accent: '#f7e5d8',
    deep: '#7b3219',
  },
  {
    name: 'Laois Business Summit',
    url: 'https://laoisbusinesssummit.ie/',
    domain: 'laoisbusinesssummit.ie',
    category: 'Conference & Community',
    number: '04',
    accent: '#e4e9f3',
    deep: '#263a65',
  },
  {
    name: 'Occuhealth',
    url: 'https://www.occuhealth.ie/',
    domain: 'occuhealth.ie',
    category: 'Occupational Health',
    number: '05',
    previewImage: '/images/portfolio/occuhealth-home.webp',
    accent: '#e0eff0',
    deep: '#15505a',
  },
  {
    name: "Ramsbottom's Portlaoise",
    url: 'https://www.ramsportlaoise.ie/',
    domain: 'ramsportlaoise.ie',
    category: 'Hospitality & Dining',
    number: '06',
    videoSrc: 'https://www.ramsportlaoise.ie/media/videos/landing-video.mp4',
    accent: '#efe5dc',
    deep: '#4b3023',
  },
  {
    name: "Martley's of Portlaoise",
    url: 'https://martleys-website.vercel.app/#top',
    domain: 'martleys-website.vercel.app',
    category: 'Coach & Transport',
    number: '07',
    livePreview: true,
    accent: '#e2ecf9',
    deep: '#165399',
  },
];

// Match the source viewport width for a sharper cached preview without a large PNG.
const screenshots = projects.map(({ url, previewImage }) =>
  previewImage || `https://image.thum.io/get/width/1200/crop/1000/maxAge/168/allowJPG/${url}`
);

function ArrowIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10h11M11 5l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProjectShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [liveIndex, setLiveIndex] = useState(-1);
  const [playingIndex, setPlayingIndex] = useState(-1);
  const [previewWidth, setPreviewWidth] = useState(0);
  const previewRef = useRef(null);
  const screenRef = useRef(null);
  const prefetched = useRef(false);
  const preloadImages = useRef([]);
  const project = projects[activeIndex];

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setPreviewVisible(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setPreviewVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '300px' });

    observer.observe(previewRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const screen = screenRef.current;
    const updateWidth = () => setPreviewWidth(screen.clientWidth);
    updateWidth();

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(screen);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [activeIndex]);

  const preloadRemaining = () => {
    if (prefetched.current) return;
    prefetched.current = true;

    const preload = () => {
      screenshots.forEach((src, index) => {
        if (index !== activeIndex) {
          const image = new Image();
          image.fetchPriority = 'low';
          image.src = src;
          preloadImages.current.push(image);
        }
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preload, { timeout: 2000 });
    } else {
      window.setTimeout(preload, 0);
    }
  };

  const moveProject = (direction) => {
    setActiveIndex((current) => (current + direction + projects.length) % projects.length);
  };

  return (
    <section className="web-work reveal" aria-labelledby="web-work-title">
      <div className="web-work-heading">
        <div>
          <span className="web-work-kicker">Selected work</span>
          <h2 id="web-work-title">Websites built to be experienced.</h2>
        </div>
        <p>
          From first impression to final click, every detail is designed around the
          people who will actually use it. Explore a selection of our recent launches.
        </p>
      </div>

      <div className="web-work-stage">
        <div className="project-list" aria-label="Choose a project">
          {projects.map((item, index) => (
            <button
              type="button"
              key={item.domain}
              className={`project-select ${index === activeIndex ? 'active' : ''}`}
              onClick={() => setActiveIndex(index)}
              onMouseEnter={() => setActiveIndex(index)}
              aria-pressed={index === activeIndex}
            >
              <span className="project-number">{item.number}</span>
              <span className="project-select-copy">
                <strong>{item.name}</strong>
                <small>{item.category}</small>
              </span>
              <span className="project-select-arrow"><ArrowIcon /></span>
            </button>
          ))}
        </div>

        <div ref={previewRef} className="project-preview" style={{ '--project-accent': project.accent, '--project-deep': project.deep }}>
          <div className="browser-frame" key={project.domain}>
            <div
              className={`browser-screen${project.heroImage ? ' has-hero-image' : ''}`}
              ref={screenRef}
              style={project.heroImage ? {
                backgroundImage: `linear-gradient(to bottom, #000 0 6.8%, transparent 6.8%), url("${project.heroImage}")`,
              } : undefined}
            >
              <div className="preview-placeholder" aria-hidden="true">
                <span>{project.number}</span>
                <strong>{project.name}</strong>
              </div>
              <img
                src={previewVisible ? screenshots[activeIndex] : undefined}
                alt={`Homepage preview of ${project.name}`}
                decoding="async"
                onLoad={() => {
                  if (project.livePreview || project.videoSrc) {
                    setLiveIndex(activeIndex);
                  } else {
                    preloadRemaining();
                  }
                }}
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                  if (project.livePreview || project.videoSrc) {
                    setLiveIndex(activeIndex);
                  } else {
                    preloadRemaining();
                  }
                }}
              />
              {project.livePreview && liveIndex === activeIndex && previewWidth > 0 && (
                <iframe
                  title={`Live preview of ${project.name}`}
                  className={`live-preview${playingIndex === activeIndex ? ' is-ready' : ''}`}
                  src={project.url}
                  width="1200"
                  height="1000"
                  style={{ transform: `scale(${previewWidth / 1200})` }}
                  loading="lazy"
                  allow="autoplay"
                  tabIndex="-1"
                  onLoad={() => {
                    setPlayingIndex(activeIndex);
                    preloadRemaining();
                  }}
                />
              )}
              {project.videoSrc && liveIndex === activeIndex && (
                <>
                  <video
                    className={`video-preview${playingIndex === activeIndex ? ' is-ready' : ''}`}
                    src={project.videoSrc}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    onPlaying={() => {
                      setPlayingIndex(activeIndex);
                      preloadRemaining();
                    }}
                    onError={preloadRemaining}
                  />
                  <div className={`rams-video-content${playingIndex === activeIndex ? ' is-ready' : ''}`} aria-hidden="true">
                    <div className="rams-video-header">
                      <img src="/images/portfolio/ramsbottoms-mark.webp" alt="" />
                      <span>About &nbsp; History &nbsp; The Team &nbsp; Gallery &nbsp; Things To Do &nbsp; Contact</span>
                    </div>
                    <div className="rams-video-brand">
                      <img src="/images/portfolio/ramsbottoms-logo.webp" alt="" />
                      <span>A warm welcome awaits in the heart of Laois</span>
                    </div>
                  </div>
                </>
              )}
              <a href={project.url} target="_blank" rel="noopener noreferrer" className="preview-overlay">
                <span>Visit live site</span>
                <ArrowIcon />
              </a>
            </div>
          </div>

          <div className="project-preview-footer">
            <div>
              <span>{project.category}</span>
              <h3>{project.name}</h3>
            </div>
            <div className="project-controls" aria-label="Project navigation">
              <button type="button" onClick={() => moveProject(-1)} aria-label="Previous project">←</button>
              <span>{String(activeIndex + 1).padStart(2, '0')} / {String(projects.length).padStart(2, '0')}</span>
              <button type="button" onClick={() => moveProject(1)} aria-label="Next project">→</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WebDesign() {
  return (
    <div className="web-page">
      <Header />

      <section className="web-hero reveal page-content">
        <div className="web-hero-text">
          <span className="web-eyebrow">Web Design</span>
          <h1>State-of-the-art websites, delivered faster</h1>
          <p>
            Our engineers build modern, high-performing websites and get them live quicker
            than the competition, without ever cutting corners on quality.
          </p>
        </div>
        <div className="web-hero-image hover-lift">
          <img src={images.services.webDesignHero} alt="A laptop displaying a website designed by AOK" />
        </div>
      </section>

      <div className="hero-divider" aria-hidden="true"></div>

      <div className="web-features">
        {features.map((feature, i) => (
          <section
            key={feature.title}
            className={`web-feature ${i % 2 === 1 ? 'reverse reveal-right' : 'reveal-left'}`}
          >
            <div className="web-feature-image img-zoom-wrap">
              <img src={feature.image} alt={feature.alt} />
            </div>
            <div className="web-feature-text">
              <h2>{feature.title}</h2>
              <p>{feature.text}</p>
            </div>
          </section>
        ))}
      </div>

      <ProjectShowcase />

      <section className="web-cta reveal">
        <h2>Ready to get online?</h2>
        <p>Let&rsquo;s build you a fast, modern website, and get it live sooner than you&rsquo;d expect.</p>
        <Link to="/contact" className="contact-button hover-lift">Start Your Site</Link>
      </section>

      <Footer />
    </div>
  );
}

export default WebDesign;
